import { DraftEvents, MeetingRoomRequirements } from "@/lib/database/events";
import { createDraftEvent, updateDraftEvent } from "@/features/create-event/_logic/api";
import type { AgendaItem } from "@/features/create-event/_components/sections/agenda/_types/agenda-types";
import type { CreateEventFormData, EventDateOption, RoomTypeCounts } from "../_types/wizard-types";

/**
 * Persists a debounced field update to the wizard's draft row, creating the row on first write.
 * The first write carries the full initial payload in one insert (rather than an insert-then-update
 * pair), since by the time autosave fires the wizard already has more than just a name to persist.
 *
 * @param draftEventId - The current draft's id, or `null` if no draft has been created yet.
 * @param updates - Partial `draft_events` column updates for this save.
 * @returns The draft's id (existing, or newly created on first call).
 */
export async function saveDraftField(draftEventId: number | null, updates: Partial<DraftEvents>): Promise<number> {
  if (draftEventId === null) {
    const { draft_event_id } = await createDraftEvent(updates);
    return draft_event_id;
  }
  await updateDraftEvent(draftEventId, updates);
  return draftEventId;
}

/**
 * Draft-only wrapper shape stored in `draft_events.accommodations` while the wizard is in progress.
 * Distinct from the final `events.accommodations` shape (a bare per-night room-type map) — the draft
 * needs to additionally round-trip `needed`/`checkIn`/`checkOut`/the flat scalar, none of which the
 * final column carries. `buildEventInsertRowFromDraft` (event-field-mapping.ts) derives the final
 * shape from live `CreateEventFormData`, not from this column directly, so the two are free to diverge.
 */
interface DraftAccommodationsValue {
  needed: boolean;
  checkIn: string | null;
  checkOut: string | null;
  maxRoomsPerNight: number;
  detailed: { [date: string]: RoomTypeCounts } | null;
}

/** Draft-only wrapper shape stored in `draft_events.meeting_room_dates` — see {@link DraftAccommodationsValue}.
 *  Mirrors `DraftEvents["meeting_room_dates"]` exactly (kept as a named local alias for readability). */
type DraftMeetingRoomsValue = NonNullable<DraftEvents["meeting_room_dates"]>;

/**
 * Legacy fields that used to live directly on `draft_events.meeting_room_dates` before food &
 * beverage became its own step/column — not part of the current `DraftEvents["meeting_room_dates"]`
 * type (the DB column is untyped jsonb, so older rows can still carry them). Read-only: never
 * written by {@link buildDraftUpdateFromFormData}, only consulted as a resume-time fallback in
 * {@link hydrateFormDataFromDraft} for drafts autosaved before the `food_beverage_requirements`
 * column existed.
 */
interface LegacyMeetingRoomsFoodBeverage {
  foodAndBeverage?: boolean | null;
  foodAndBeverageDetails?: string;
}

/** Draft-only wrapper shape stored in `draft_events.event_dates` — see {@link DraftAccommodationsValue}. */
interface DraftEventDatesValue {
  primary: EventDateOption | null;
  alternates: EventDateOption[];
  notes: string;
}

/** Draft-only wrapper shape stored in `draft_events.budget_breakdown` — see
 *  {@link DraftAccommodationsValue}. Adds `unknown` (the "I'm not sure yet" toggle), which has no
 *  equivalent on the final `events.budget_breakdown` column. */
interface DraftBudgetValue {
  venue: number | null;
  unknown: boolean;
  flights: number | null;
  foodBeverage: number | null;
  transportation: number | null;
  activities: number | null;
  other: number | null;
}

/**
 * Draft-only wrapper shape stored in `draft_events.agenda` — see {@link DraftAccommodationsValue}.
 * Kept as a wrapper object (rather than a bare array matching `DraftEvents["agenda"]`) so already-saved
 * draft rows continue to round-trip: `agenda`'s real column shape is an array of sessions, but this
 * wrapper has been the draft-only shape since before this field existed.
 */
interface DraftAgendaValue {
  items: AgendaItem[];
}

/**
 * Maps the wizard's fields to a `draft_events` column update for autosave.
 * Fields with no natural 1:1 column (the detail-grid toggles, ranked date alternates) are packed into
 * a draft-only wrapper object on the closest related jsonb column — see the wrapper interfaces above.
 *
 * @param formData - Current wizard form data.
 * @returns Partial `draft_events` update ready for `saveDraftField`.
 */
export function buildDraftUpdateFromFormData(formData: CreateEventFormData): Partial<DraftEvents> {
  const accommodationsValue: DraftAccommodationsValue = {
    needed: formData.accommodations.needed,
    checkIn: formData.accommodations.checkIn,
    checkOut: formData.accommodations.checkOut,
    maxRoomsPerNight: formData.accommodations.maxRoomsPerNight,
    detailed: formData.accommodationsDetailed,
  };

  const meetingRoomsValue: DraftMeetingRoomsValue = {
    needed: formData.meetingRooms.needed,
    start: formData.meetingRooms.dates.start,
    end: formData.meetingRooms.dates.end,
    numRooms: formData.meetingRooms.numRooms,
    // `detailed` is a legacy per-day toggle this wizard version no longer collects (superseded by
    // the general-session/breakout breakdown). No step writes it, so it's echoed back from
    // `meetingRoomsDetailedLegacy` (hydrated from the draft, untouched since) rather than hardcoded
    // null — this column is a full-value jsonb replace on every write, so hardcoding null here would
    // silently wipe real per-day data on the very next autosave after resuming an older draft.
    detailed: formData.meetingRoomsDetailedLegacy,
  };

  // Same shape as `events.meeting_room_requirements` (see `resolveMeetingRoomRequirements` in
  // event-field-mapping.ts) — no draft-only wrapper needed since the wizard's general-session/
  // breakout fields already match the final column 1:1.
  const meetingRoomRequirementsValue: MeetingRoomRequirements = {
    generalSession: formData.meetingRooms.generalSession,
    breakout: formData.meetingRooms.breakout,
  };

  // Food & beverage is its own step now, independent of meeting rooms — round-tripped through the
  // existing `food_beverage_requirements` column using the same Yes/No/free-text sentinel `meeting_rooms`
  // already uses above, since a plain string column can't carry `needed === null` (unanswered) as
  // distinct from `needed === false` (answered "No") any other way.
  const foodBeverageRequirementsValue =
    formData.foodBeverage.needed === null
      ? undefined
      : formData.foodBeverage.needed
        ? formData.foodBeverage.details.trim() || "Yes"
        : "No";

  const eventDatesValue: DraftEventDatesValue = {
    primary: formData.eventDates,
    alternates: formData.eventDatesAlternates,
    notes: formData.eventDatesNotes,
  };

  const budgetValue: DraftBudgetValue = {
    venue: formData.budgetVenue,
    unknown: formData.budgetUnknown,
    flights: formData.budgetFlights,
    foodBeverage: formData.budgetFoodBeverage,
    transportation: formData.budgetTransportation,
    activities: formData.budgetActivities,
    other: formData.budgetOther,
  };

  const agendaValue: DraftAgendaValue = {
    items: formData.agendaItems,
  };

  return {
    name: formData.name,
    formatted_locations: formData.formattedLocations,
    num_people: formData.numPeople,
    accommodations: accommodationsValue as unknown as DraftEvents["accommodations"],
    meeting_rooms: formData.meetingRooms.needed ? "Yes" : "No",
    food_beverage_requirements: foodBeverageRequirementsValue,
    num_meeting_rooms_needed: formData.meetingRooms.numRooms,
    agenda: agendaValue as unknown as DraftEvents["agenda"],
    meeting_room_dates: meetingRoomsValue,
    meeting_room_requirements: meetingRoomRequirementsValue,
    event_dates: eventDatesValue as unknown as DraftEvents["event_dates"],
    flexibility: formData.flexibility ?? undefined,
    budget_breakdown: budgetValue as unknown as DraftEvents["budget_breakdown"],
    notes_location_budget: formData.notesLocationBudget,
    decision_deadline: formData.decisionDeadline ? new Date(formData.decisionDeadline) : undefined,
    decision_factors: formData.decisionFactors,
    decision_factor_other_text: formData.decisionFactorOtherText ?? undefined,
    additional_services: formData.additionalServices,
    custom_questions: formData.customQuestions,
    custom_concessions: formData.customConcessions,
    event_source: formData.eventSource ?? undefined,
    event_source_other_text: formData.eventSourceOtherText ?? undefined,
    other_notes: formData.additionalNotes,
  };
}

/**
 * Inverse of {@link buildDraftUpdateFromFormData} — hydrates the wizard's fields from a loaded
 * draft row. Missing/malformed wrapper values fall back to safe defaults rather than throwing, since a
 * draft from an older wizard schema (or a hand-edited row) shouldn't crash resume.
 *
 * @param draft - The loaded draft row.
 * @returns Partial `CreateEventFormData` for the `HYDRATE_DRAFT` action.
 */
export function hydrateFormDataFromDraft(draft: DraftEvents): Partial<CreateEventFormData> {
  const accommodationsValue = (draft.accommodations as unknown as DraftAccommodationsValue) ?? null;
  const meetingRoomsValue = draft.meeting_room_dates ?? null;
  const meetingRoomRequirementsValue = draft.meeting_room_requirements ?? null;
  const eventDatesValue = (draft.event_dates as unknown as DraftEventDatesValue) ?? null;
  const budgetValue = (draft.budget_breakdown as unknown as DraftBudgetValue) ?? null;
  const agendaValue = (draft.agenda as unknown as DraftAgendaValue) ?? null;
  const formattedLocations = draft.formatted_locations ?? [];

  return {
    draftEventId: draft.draft_event_id,
    name: draft.name ?? "",
    formattedLocations,
    numPeople: draft.num_people ?? 0,
    accommodations: {
      needed: accommodationsValue?.needed ?? false,
      checkIn: accommodationsValue?.checkIn ?? null,
      checkOut: accommodationsValue?.checkOut ?? null,
      maxRoomsPerNight: accommodationsValue?.maxRoomsPerNight ?? 0,
    },
    accommodationsDetailed: accommodationsValue?.detailed ?? null,
    meetingRoomsDetailedLegacy: meetingRoomsValue?.detailed ?? null,
    meetingRooms: {
      needed: meetingRoomsValue?.needed ?? null,
      dates: { start: meetingRoomsValue?.start ?? null, end: meetingRoomsValue?.end ?? null },
      numRooms: meetingRoomsValue?.numRooms ?? 0,
      generalSession: {
        needed: meetingRoomRequirementsValue?.generalSession?.needed ?? null,
        roomCount: meetingRoomRequirementsValue?.generalSession?.roomCount ?? 0,
        capacity: meetingRoomRequirementsValue?.generalSession?.capacity ?? null,
        roomTypes: meetingRoomRequirementsValue?.generalSession?.roomTypes ?? [],
        notes: meetingRoomRequirementsValue?.generalSession?.notes ?? "",
      },
      breakout: {
        needed: meetingRoomRequirementsValue?.breakout?.needed ?? null,
        count: meetingRoomRequirementsValue?.breakout?.count ?? 0,
        capacityMin: meetingRoomRequirementsValue?.breakout?.capacityMin ?? null,
        capacityMax: meetingRoomRequirementsValue?.breakout?.capacityMax ?? null,
        roomTypes: meetingRoomRequirementsValue?.breakout?.roomTypes ?? [],
        notes: meetingRoomRequirementsValue?.breakout?.notes ?? "",
      },
    },
    // Inverse of the Yes/No/free-text sentinel `buildDraftUpdateFromFormData` writes to this same
    // column — mirrors how `meeting_rooms` encodes its own needed/unanswered distinction. Falls
    // back to the legacy `meeting_room_dates.foodAndBeverage(Details)` fields (see
    // `LegacyMeetingRoomsFoodBeverage`) for drafts autosaved before `food_beverage_requirements`
    // existed as its own column — without this, resuming one of those drafts silently resets a
    // previously-answered F&B question to unanswered.
    foodBeverage: (() => {
      if (draft.food_beverage_requirements != null) {
        return draft.food_beverage_requirements === "No"
          ? { needed: false, details: "" }
          : {
              needed: true,
              details: draft.food_beverage_requirements === "Yes" ? "" : draft.food_beverage_requirements,
            };
      }
      const legacy = (draft.meeting_room_dates as unknown as LegacyMeetingRoomsFoodBeverage | null) ?? null;
      if (legacy?.foodAndBeverage == null) return { needed: null, details: "" };
      return {
        needed: legacy.foodAndBeverage,
        details: legacy.foodAndBeverage ? (legacy.foodAndBeverageDetails ?? "") : "",
      };
    })(),
    eventDates: eventDatesValue?.primary ?? null,
    eventDatesAlternates: eventDatesValue?.alternates ?? [],
    eventDatesNotes: eventDatesValue?.notes ?? "",
    flexibility: draft.flexibility ?? null,
    budgetVenue: budgetValue?.venue ?? null,
    budgetUnknown: budgetValue?.unknown ?? false,
    budgetFlights: budgetValue?.flights ?? null,
    budgetFoodBeverage: budgetValue?.foodBeverage ?? null,
    budgetTransportation: budgetValue?.transportation ?? null,
    budgetActivities: budgetValue?.activities ?? null,
    budgetOther: budgetValue?.other ?? null,
    notesLocationBudget: draft.notes_location_budget ?? "",
    decisionDeadline: draft.decision_deadline ? new Date(draft.decision_deadline).toISOString().slice(0, 10) : null,
    decisionFactors: draft.decision_factors ?? [],
    decisionFactorOtherText: draft.decision_factor_other_text ?? null,
    additionalServices: draft.additional_services ?? [],
    agendaItems: agendaValue?.items ?? [],
    customQuestions: draft.custom_questions ?? [],
    customConcessions: draft.custom_concessions ?? [],
    eventSource: draft.event_source ?? null,
    eventSourceOtherText: draft.event_source_other_text ?? null,
    additionalNotes: draft.other_notes ?? "",
  };
}
